import { createReducer, on } from '@ngrx/store';
import { ApiEndpointType } from '../utilities/Constants';
import { AchActions, CheckActions, InternalTransferActions, MasterStatusViewActions, PtrsActions, WireActions } from '../../create-request/actions';
import { ViewPage } from '../../create-request/models/view-page/ViewPage';
import { ViewPageActions } from '../../transaction-view/actions';
import { StandingAuthMasterStatusViewActions } from '../../standing-authorizations/actions';

export const viewPageKey = 'viewPage';

export type State = ViewPage;

export const initialViewPage: State = {
  loading: false,
  error: '',
  accountDetails: {
    subAccountId: '',
    regType: '',
    accountHolderLine1: '',
    accountHolderLine2: '',
    accountHolderLine3: '',
    isIra: false
  },
  hasError: false,
  taxId: '',
  serviceError: {
    status: 0,
    url: '',
    name: '',
    message: '',
    correlationId: '',
    hasError: false
  }
};

export const viewPageReducer = createReducer(
  initialViewPage,
  on(
    AchActions.callInquiry,
    InternalTransferActions.callInquiry,
    WireActions.callInquiry,
    CheckActions.callInquiry,
    MasterStatusViewActions.callMsvInternalTransferInquiry,
    MasterStatusViewActions.callMsvCheckInquiry,
    MasterStatusViewActions.callMsvAchInquiry,
    MasterStatusViewActions.callMsvWireInquiry,
    StandingAuthMasterStatusViewActions.callSaMsvInternalTransferInquiry,
    StandingAuthMasterStatusViewActions.callSaMsvCheckInquiry,
    StandingAuthMasterStatusViewActions.callSaMsvAchInquiry,
    StandingAuthMasterStatusViewActions.callSaMsvWireInquiry,
    (state) => {
      return {
        ...state,
        loading: true,
        error: '',
        hasError: false,
        serviceError: {
          ...state.serviceError,
          status: 0,
          url: '',
          name: '',
          message: '',
          hasError: false,
        },
      };
    }
  ),
  on(
    AchActions.inquiryCallSuccessful,
    InternalTransferActions.inquiryCallSuccessful,
    WireActions.inquiryCallSuccessful,
    CheckActions.inquiryCallSuccessful,
    CheckActions.incomingInquiryCallSuccessful,
    (state) => {
      return {
        ...state,
        loading: false,
        error: '',
        hasError: false,
        taxId: state.taxId,
        serviceError: {
          ...state.serviceError,
          status: 0,
          url: '',
          name: '',
          message: '',
          hasError: false,
        },
      };
    }
  ),
  on(AchActions.inquiryCallSuccessful, (state, response) => {
    return {
      ...state,
      taxId: response.achInquiryResponse.taxId,
      accountDetails: {
        subAccountId:
          response.achInquiryResponse.accountRegistration.subAccountNumber,
        isIra: response.achInquiryResponse.isIraTransaction,
        regType: response.achInquiryResponse.accountRegistration.regShortName,
        accountHolderLine1:
          response.achInquiryResponse.accountRegistration.subAccountNameLine1,
        accountHolderLine2:
          response.achInquiryResponse.accountRegistration.subAccountNameLine2,
        accountHolderLine3:
          response.achInquiryResponse.accountRegistration.subAccountNameLine3,
      },
    };
  }),
  on(
    CheckActions.inquiryCallSuccessful,
    CheckActions.incomingInquiryCallSuccessful,
    (state, response) => {
      return {
        ...state,
        taxId: response.checkInquiryResponse.taxId,
        accountDetails: {
          subAccountId:
            response.checkInquiryResponse.cashieringCheckInputDetails
              .accountDisplayRecord.subAccountNumber,
          regType:
            response.checkInquiryResponse.cashieringCheckInputDetails
              .accountDisplayRecord.regShortName,
          isIra: response.checkInquiryResponse.iraaccount,
          accountHolderLine1:
            response.checkInquiryResponse.cashieringCheckInputDetails
              .accountDisplayRecord.subAccountNameLine1,
          accountHolderLine2:
            response.checkInquiryResponse.cashieringCheckInputDetails
              .accountDisplayRecord.subAccountNameLine2,
          accountHolderLine3:
            response.checkInquiryResponse.cashieringCheckInputDetails
              .accountDisplayRecord.subAccountNameLine3,
        },
      };
    }
  ),
  on(InternalTransferActions.inquiryCallSuccessful, (state, response) => {
    return {
      ...state,
      taxId: response.internalTransferInquiryResponse.taxId,
      accountDetails: {
        subAccountId:
          response.internalTransferInquiryResponse.accountInfoBanner
            .subAccountNumber,
        regType:
          response.internalTransferInquiryResponse.accountInfoBanner
            .regShortName,
        isIra: response.internalTransferInquiryResponse.iraFields.isIraTransaction,
        accountHolderLine1:
          response.internalTransferInquiryResponse.accountInfoBanner
            .subAccountNameLine1,
        accountHolderLine2:
          response.internalTransferInquiryResponse.accountInfoBanner
            .subAccountNameLine2,
        accountHolderLine3:
          response.internalTransferInquiryResponse.accountInfoBanner
            .subAccountNameLine3,
      },
    };
  }),
  on(CheckActions.inquiryCallSuccessful, (state, response) => {
    let { accountDisplayRecord } =
      response.checkInquiryResponse.cashieringCheckInputDetails;
    return {
      ...state,
      taxId: response.checkInquiryResponse.taxId,
      accountDetails: {
        subAccountId: accountDisplayRecord.subAccountNumber,
        regType: accountDisplayRecord.regShortName,
        isIra: response.checkInquiryResponse.iraaccount,
        accountHolderLine1: accountDisplayRecord.subAccountNameLine1,
        accountHolderLine2: accountDisplayRecord.subAccountNameLine2,
        accountHolderLine3: accountDisplayRecord.subAccountNameLine3,
      },
    };
  }),
  on(WireActions.inquiryCallSuccessful, (state, response) => {
    return {
      ...state,
      taxId: response.wireInquiryResponse.taxId,
      accountDetails: {
        subAccountId: response.wireInquiryResponse.accountDisplayRecord.subAccountNumber,
        regType: response.wireInquiryResponse.accountDisplayRecord.regShortName,
        isIra: response.wireInquiryResponse.isIRATransaction,
        accountHolderLine1: response.wireInquiryResponse.accountDisplayRecord.subAccountNameLine1,
        accountHolderLine2: response.wireInquiryResponse.accountDisplayRecord.subAccountNameLine2,
        accountHolderLine3: response.wireInquiryResponse.accountDisplayRecord.subAccountNameLine3
      },
      receivingAccountHolderIdentifierList: response.wireInquiryResponse.isoUIFields?.recipient?.identifierList
    };
  }),
  on(AchActions.inquiryCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      error: '',
      hasError: true,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.moneylinkInquiry,
        message: err.message,
        hasError: !err.ok,
      },
    };
  }),
  on(InternalTransferActions.inquiryCallFail, (state, err) => {
    return {
      ...state,
      loading: false,
      error: '',
      hasError: true,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.internalTransferInquiry,
        message: err.message,
        hasError: !err.ok,
      },
    };
  }),
  on(WireActions.inquiryCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      error: '',
      hasError: true,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.wireInquiry,
        message: err.message,
        hasError: !err.ok,
      },
    };
  }),
  on(CheckActions.inquiryCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      error: '',
      hasError: true,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.checkInquiry,
        message: err.message,
        hasError: !err.ok,
      },
    };
  }),
  on(PtrsActions.transInquiryCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      error: '',
      hasError: true,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.transInquiry,
        message: err.message,
        hasError: !err.ok,
      },
    };
  }),
  on(ViewPageActions.backButtonClicked,
    ViewPageActions.backButtonClickedFromMsv,
    (state) => {
    return {
      ...state,
      accountDetails: {
        subAccountId: '',
        regType: state.accountDetails?.regType ?? '',
        accountHolderLine1: state.accountDetails?.accountHolderLine1 ?? '',
        accountHolderLine2: state.accountDetails?.accountHolderLine2 ?? '',
        accountHolderLine3: state.accountDetails?.accountHolderLine3 ?? '',
        isIra: state.accountDetails?.isIra ?? false
      },
      taxId: '',
    }
  })
);
